import { useLang } from "@/context/LangContext";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Mail, Trash2, AlertCircle } from "lucide-react";

export default function DataDeletion() {
  const { lang, isRTL } = useLang();
  const navigate = useNavigate();
  const Chev = isRTL ? ChevronRight : ChevronLeft;

  return (
    <div className="min-h-screen bg-white" data-testid="data-deletion-page">
      <div className="sticky top-0 bg-white border-b border-[#EAEAEA] z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full hover:bg-[#F5F5F5] flex items-center justify-center"
            data-testid="data-deletion-back-btn"
          >
            <Chev className="w-5 h-5" />
          </button>
          <h1 className="font-bold text-lg">
            {lang === "ar" ? "حذف الحساب والبيانات" : "Account & Data Deletion"}
          </h1>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-5 py-6 text-[#1A1A1A] leading-relaxed">
        {lang === "ar" ? (
          <div className="space-y-5 text-[14px]">
            <div className="bg-[#FFF4F0] border border-[#FFD8C9] rounded-2xl p-4 flex gap-3">
              <Trash2 className="w-6 h-6 text-[#E05D3A] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-[15px] text-[#E05D3A] mb-1">
                  طلب حذف الحساب والبيانات الشخصية
                </p>
                <p className="text-[13px] text-[#3D3D3D]">
                  يحق لك حذف حسابك وجميع بياناتك الشخصية من تطبيق جايك في أي وقت.
                </p>
              </div>
            </div>

            <Section title="١. كيف تطلب حذف حسابك">
              <p>يوجد طريقتان لحذف حسابك:</p>
              <div className="space-y-2 mt-2">
                <div className="bg-[#F8F8F8] rounded-xl p-3">
                  <p className="font-bold text-[14px] mb-1">الطريقة الأولى: من داخل التطبيق</p>
                  <ol className="list-decimal ps-5 space-y-1 text-[13px]">
                    <li>افتح التطبيق وسجّل الدخول</li>
                    <li>اذهب إلى <b>الملف الشخصي</b></li>
                    <li>اضغط <b>تواصل معنا</b></li>
                    <li>اطلب حذف الحساب</li>
                  </ol>
                </div>

                <div className="bg-[#F8F8F8] rounded-xl p-3">
                  <p className="font-bold text-[14px] mb-1">الطريقة الثانية: عبر البريد الإلكتروني</p>
                  <p className="text-[13px]">
                    أرسل طلب حذف من البريد المسجّل في حسابك إلى:{" "}
                    <a
                      href="mailto:support@jayek.sa?subject=طلب حذف الحساب"
                      className="text-[#E05D3A] font-bold inline-flex items-center gap-1"
                      data-testid="delete-email-link"
                    >
                      <Mail className="w-4 h-4" />
                      support@jayek.sa
                    </a>
                  </p>
                  <p className="text-[12px] text-[#6B6B6B] mt-1">
                    اكتب في عنوان الرسالة: "طلب حذف الحساب" مع رقم جوالك أو إيميلك المسجّل.
                  </p>
                </div>
              </div>
            </Section>

            <Section title="٢. البيانات التي يتم حذفها">
              <ul className="list-disc ps-5 space-y-1">
                <li>الاسم والبريد الإلكتروني ورقم الجوال</li>
                <li>كلمة المرور والتوكنات</li>
                <li>العناوين المحفوظة</li>
                <li>المفضّلة والكوبونات والمحفظة</li>
                <li>الإشعارات وسجل الجلسات</li>
                <li>توكنات FCM الخاصة بجهازك</li>
              </ul>
            </Section>

            <Section title="٣. البيانات التي يتم الاحتفاظ بها">
              <p>لأسباب قانونية ومحاسبية، نحتفظ بالبيانات التالية لمدة <b>5 سنوات</b> (متطلبات نظام ضريبة القيمة المضافة في السعودية):</p>
              <ul className="list-disc ps-5 space-y-1 mt-2">
                <li>سجل الطلبات والفواتير (بدون ربط بهويتك الشخصية)</li>
                <li>سجلات الدفع المالية</li>
              </ul>
              <p className="mt-2 text-[12px] text-[#6B6B6B]">
                هذه البيانات يتم إخفاء هويتك (anonymization) فيها بعد حذف الحساب.
              </p>
            </Section>

            <Section title="٤. مدة المعالجة">
              <div className="bg-[#F0F7FF] border border-[#CFE3FF] rounded-xl p-3 flex gap-2">
                <AlertCircle className="w-5 h-5 text-[#1A73E8] flex-shrink-0 mt-0.5" />
                <p className="text-[13px]">
                  تتم معالجة طلب الحذف خلال <b>30 يوم عمل</b> كحد أقصى من تاريخ استلام الطلب. ستصلك رسالة تأكيد على بريدك بعد إتمام الحذف.
                </p>
              </div>
            </Section>

            <Section title="٥. التواصل">
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#E05D3A]" />
                <a className="text-[#E05D3A] font-bold" href="mailto:support@jayek.sa">
                  support@jayek.sa
                </a>
              </p>
            </Section>

            <p className="text-xs text-[#6B6B6B] pt-3 border-t border-[#EAEAEA]">
              آخر تحديث: فبراير 2026
            </p>
          </div>
        ) : (
          <div className="space-y-5 text-[14px]">
            <div className="bg-[#FFF4F0] border border-[#FFD8C9] rounded-2xl p-4 flex gap-3">
              <Trash2 className="w-6 h-6 text-[#E05D3A] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-[15px] text-[#E05D3A] mb-1">
                  Account & Personal Data Deletion Request
                </p>
                <p className="text-[13px] text-[#3D3D3D]">
                  You have the right to delete your account and all your personal data from Jayek at any time.
                </p>
              </div>
            </div>

            <Section title="1. How to Request Account Deletion">
              <p>There are two ways to delete your account:</p>
              <div className="space-y-2 mt-2">
                <div className="bg-[#F8F8F8] rounded-xl p-3">
                  <p className="font-bold text-[14px] mb-1">Method 1: From the App</p>
                  <ol className="list-decimal ps-5 space-y-1 text-[13px]">
                    <li>Open the app and log in</li>
                    <li>Go to <b>Profile</b></li>
                    <li>Tap <b>Contact Us</b></li>
                    <li>Request account deletion</li>
                  </ol>
                </div>

                <div className="bg-[#F8F8F8] rounded-xl p-3">
                  <p className="font-bold text-[14px] mb-1">Method 2: By Email</p>
                  <p className="text-[13px]">
                    Send a deletion request from your registered email to:{" "}
                    <a
                      href="mailto:support@jayek.sa?subject=Account Deletion Request"
                      className="text-[#E05D3A] font-bold inline-flex items-center gap-1"
                    >
                      <Mail className="w-4 h-4" />
                      support@jayek.sa
                    </a>
                  </p>
                  <p className="text-[12px] text-[#6B6B6B] mt-1">
                    Subject: "Account Deletion Request" — include your registered phone or email.
                  </p>
                </div>
              </div>
            </Section>

            <Section title="2. What Will Be Deleted">
              <ul className="list-disc ps-5 space-y-1">
                <li>Name, email, and phone number</li>
                <li>Password and authentication tokens</li>
                <li>Saved addresses</li>
                <li>Favorites, coupons, and wallet balance</li>
                <li>Notifications and session history</li>
                <li>FCM device tokens</li>
              </ul>
            </Section>

            <Section title="3. Data Retained (Required by Law)">
              <p>
                For legal and accounting reasons, the following data is retained for <b>5 years</b> (Saudi VAT regulations):
              </p>
              <ul className="list-disc ps-5 space-y-1 mt-2">
                <li>Order history and invoices (anonymized)</li>
                <li>Financial payment records</li>
              </ul>
              <p className="mt-2 text-[12px] text-[#6B6B6B]">
                This data is anonymized after account deletion to remove your identity.
              </p>
            </Section>

            <Section title="4. Processing Time">
              <div className="bg-[#F0F7FF] border border-[#CFE3FF] rounded-xl p-3 flex gap-2">
                <AlertCircle className="w-5 h-5 text-[#1A73E8] flex-shrink-0 mt-0.5" />
                <p className="text-[13px]">
                  Deletion requests are processed within <b>30 business days</b> maximum. You will receive a confirmation email once deletion is complete.
                </p>
              </div>
            </Section>

            <Section title="5. Contact">
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#E05D3A]" />
                <a className="text-[#E05D3A] font-bold" href="mailto:support@jayek.sa">
                  support@jayek.sa
                </a>
              </p>
            </Section>

            <p className="text-xs text-[#6B6B6B] pt-3 border-t border-[#EAEAEA]">
              Last updated: February 2026
            </p>
          </div>
        )}
      </article>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-bold text-[15px] text-[#E05D3A] mb-2">{title}</h2>
      <div className="text-[#1A1A1A] leading-relaxed space-y-1.5">{children}</div>
    </section>
  );
}
